import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Inbox, Archive, Mail, Phone, Send, ArchiveRestore,
  Check, Loader2, MessageSquare, ChevronRight, X,
} from "lucide-react";

const OBJECTIF_LABEL = {
  reprise_activite: "Reprise d'activité",
  remise_en_forme: "Remise en forme",
  sante_bien_etre: "Santé & Bien-être",
  sport_performance: "Sport & Performance",
};

export default function AdminContacts() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("todo");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/contacts");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const { todo, archived } = useMemo(() => {
    const todo = items.filter((c) => c.status !== "archived");
    const archived = items.filter((c) => c.status === "archived");
    return { todo, archived };
  }, [items]);

  const list = tab === "todo" ? todo : archived;
  const selected = items.find((c) => c.id === selectedId) || null;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      toast.success(status === "archived" ? "Demande archivée" : status === "new" ? "Replacée à traiter" : "Mise à jour");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Boîte de réception</p>
      <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-8">Demandes de renseignements</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <TabBtn
          active={tab === "todo"}
          onClick={() => { setTab("todo"); setSelectedId(null); }}
          icon={Inbox}
          label="À traiter"
          count={todo.length}
          testid="contacts-tab-todo"
        />
        <TabBtn
          active={tab === "archived"}
          onClick={() => { setTab("archived"); setSelectedId(null); }}
          icon={Archive}
          label="Archivés"
          count={archived.length}
          testid="contacts-tab-archived"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-fegreen mx-auto" /></div>
      ) : list.length === 0 ? (
        <div className="fe-card p-12 text-center text-fegray">
          {tab === "todo" ? "Aucune demande à traiter pour le moment." : "Aucune demande archivée."}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* List */}
          <div className="lg:col-span-2 space-y-2">
            {list.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                data-testid={`contact-row-${c.id}`}
                className={`w-full text-left fe-card p-4 transition-all ${
                  selectedId === c.id ? "ring-2 ring-fegreen border-fegreen" : ""
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-fegray-dark truncate">{c.prenom} {c.nom}</div>
                    <div className="text-xs text-fegray truncate mt-0.5">
                      {OBJECTIF_LABEL[c.objectif] || c.objectif?.replace(/_/g, " ")}
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </div>
                <div className="flex justify-between items-baseline mt-3">
                  <span className="text-[10px] text-fegray/60">
                    {new Date(c.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <ChevronRight className="w-4 h-4 text-fegray/40" />
                </div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
            {selected ? (
              <ContactDetail
                key={selected.id}
                contact={selected}
                onArchive={() => setStatus(selected.id, "archived")}
                onUnarchive={() => setStatus(selected.id, "new")}
                onSent={load}
              />
            ) : (
              <div className="fe-card p-10 text-center text-fegray">
                <MessageSquare className="w-10 h-10 mx-auto text-fegray/30 mb-3" />
                <p className="text-sm">Sélectionnez une demande pour la lire et y répondre.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
        active
          ? "bg-fegreen text-white shadow-md"
          : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <span className={`text-[10px] font-extrabold rounded-full px-2 py-0.5 ${
        active ? "bg-white text-fegreen" : "bg-fegreen-light text-fegreen"
      }`}>{count}</span>
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    new: { label: "Nouveau", cls: "bg-yellow-100 text-yellow-700" },
    in_progress: { label: "En cours", cls: "bg-blue-100 text-blue-700" },
    handled: { label: "Traité", cls: "bg-fegreen-light text-fegreen" },
    archived: { label: "Archivé", cls: "bg-gray-100 text-fegray" },
  };
  const c = map[status] || map.new;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${c.cls}`}>
      {c.label}
    </span>
  );
}

function ContactDetail({ contact, onArchive, onUnarchive, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const replies = contact.replies || [];

  const sendReply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/contacts/${contact.id}/reply`, { body });
      // Open the mail client with the reply pre-filled (no SMTP server here)
      const subject = encodeURIComponent(`Re : Votre demande FORM'ELEC – ${OBJECTIF_LABEL[contact.objectif] || ""}`);
      const mailBody = encodeURIComponent(`Bonjour ${contact.prenom},\n\n${body}\n\n— Rosalie · FORM'ELEC`);
      window.location.href = `mailto:${contact.email}?subject=${subject}&body=${mailBody}`;
      toast.success("Réponse enregistrée — votre client de messagerie a été ouvert");
      setBody("");
      await onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fe-card p-6 lg:p-7" data-testid={`contact-detail-${contact.id}`}>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-5 pb-5 border-b border-gray-100">
        <div>
          <div className="text-lg font-bold text-fegray-dark">{contact.prenom} {contact.nom}</div>
          <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-fegray">
            <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 hover:text-fegreen">
              <Mail className="w-3 h-3" /> {contact.email}
            </a>
            <a href={`tel:${contact.telephone}`} className="inline-flex items-center gap-1 hover:text-fegreen">
              <Phone className="w-3 h-3" /> {contact.telephone}
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={contact.status} />
          {contact.status === "archived" ? (
            <button
              onClick={onUnarchive}
              data-testid={`contact-unarchive-${contact.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-fegray-dark text-xs font-semibold hover:border-fegreen hover:text-fegreen"
            >
              <ArchiveRestore className="w-3 h-3" /> Désarchiver
            </button>
          ) : (
            <button
              onClick={onArchive}
              data-testid={`contact-archive-${contact.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-fegray-dark text-xs font-semibold hover:border-fegreen hover:text-fegreen"
            >
              <Archive className="w-3 h-3" /> Archiver
            </button>
          )}
        </div>
      </div>

      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-1">Objectif</p>
        <p className="text-sm text-fegray-dark">
          {OBJECTIF_LABEL[contact.objectif] || contact.objectif?.replace(/_/g, " ")}
        </p>
      </div>

      {contact.message && (
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-2">Message du client</p>
          <div className="bg-fegray-light rounded-xl p-4 text-sm text-fegray-dark leading-relaxed whitespace-pre-wrap">
            {contact.message}
          </div>
        </div>
      )}

      {replies.length > 0 && (
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-2">
            Vos réponses ({replies.length})
          </p>
          <div className="space-y-2">
            {replies.map((rep) => (
              <div key={rep.id} className="border-l-2 border-fegreen pl-3 py-1" data-testid={`contact-reply-${rep.id}`}>
                <div className="text-[10px] text-fegray/70 uppercase tracking-wider mb-1">
                  {rep.by} · {new Date(rep.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
                <p className="text-sm text-fegray-dark whitespace-pre-wrap">{rep.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={sendReply} className="border-t border-gray-100 pt-5">
        <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-2">
          Répondre à {contact.prenom}
        </p>
        <textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid={`contact-reply-body-${contact.id}`}
          placeholder={`Bonjour ${contact.prenom},\n\nMerci pour votre message. Je vous propose…`}
          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button
            type="submit"
            disabled={sending || !body.trim()}
            data-testid={`contact-reply-send-${contact.id}`}
            className="btn-green disabled:opacity-50 !py-2.5 !px-5 text-sm"
          >
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Envoyer la réponse</>}
          </button>
        </div>
        <p className="text-[10px] text-fegray/60 mt-2 leading-relaxed">
          La réponse est enregistrée dans le dossier et votre client de messagerie s'ouvre avec un brouillon prêt à envoyer à {contact.email}.
        </p>
      </form>
    </div>
  );
}
